/**
 * Author: Andrey Morozov
 * Confidential. Proprietary and confidential. Unauthorized copying or distribution is prohibited.
 */

/**
 * useSessionStore — the IAM.Core Console's session & workspace state.
 *
 * It owns the selected workspace, the active agent session, the tenant's tool
 * catalog, and the run / cancel / approve actions. All gateway I/O goes
 * through the injected GatewayClient, so the hook is unit-testable with a fake
 * client and has no hidden global state.
 *
 * The gateway runs a session synchronously (the Wave-1 transport): runSession
 * returns the terminal session with its full audit trail in one response.
 * Streaming over SSE/WebSocket is Wave-2 gateway work; until then the store
 * also exposes refresh() so a UI can re-poll a session by id.
 */

import type { Workspace } from "@iam/design-system/agent";
import { useCallback, useMemo, useState } from "react";
import type { GatewayClient } from "./gatewayClient";
import type { ApprovalDecision, SessionMode, SessionView, ToolDescriptor } from "./types";

export type UseSessionStoreOptions = {
  client: GatewayClient;
  workspaces: ReadonlyArray<Workspace>;
  principal: string;
  /** Default budget applied to every run. */
  defaultBudget?: { max_iterations?: number; max_cost_usd?: number };
};

export type SessionStore = {
  workspaceId: string;
  selectWorkspace: (id: string) => void;
  session: SessionView | null;
  running: boolean;
  error: string | null;
  tools: ToolDescriptor[];
  runSession: (goal: string, mode: SessionMode) => Promise<void>;
  cancelSession: () => Promise<void>;
  refresh: () => Promise<void>;
  loadTools: () => Promise<void>;
  decideApproval: (
    approvalId: string,
    decision: ApprovalDecision,
    comment?: string,
  ) => Promise<void>;
  clearError: () => void;
};

const DEFAULT_BUDGET = { max_iterations: 12, max_cost_usd: 1 };

/** Normalize an unknown thrown value into a user-visible error string. */
function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/** React store hook that runs sessions, streams events, and tracks approvals via GatewayClient. */
export function useSessionStore(opts: UseSessionStoreOptions): SessionStore {
  const { client, workspaces, principal } = opts;
  const budget = opts.defaultBudget ?? DEFAULT_BUDGET;

  const [workspaceId, setWorkspaceId] = useState<string>(() => workspaces[0]?.id ?? "");
  const [session, setSession] = useState<SessionView | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tools, setTools] = useState<ToolDescriptor[]>([]);

  const selectWorkspace = useCallback((id: string) => {
    setWorkspaceId(id);
    // A session belongs to one workspace — switching context resets it.
    setSession(null);
    setError(null);
  }, []);

  const runSession = useCallback(
    async (goal: string, mode: SessionMode) => {
      if (goal.trim() === "") {
        setError("Goal is required to run an agent session.");
        return;
      }
      setRunning(true);
      setError(null);
      try {
        const result = await client.runSession({
          goal: goal.trim(),
          mode,
          workspace_id: workspaceId,
          started_by: principal,
          scopes: [],
          budget,
        });
        setSession(result);
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setRunning(false);
      }
    },
    [client, workspaceId, principal, budget],
  );

  const refresh = useCallback(async () => {
    if (!session) return;
    try {
      const fresh = await client.getSession(session.id);
      setSession(fresh);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [client, session]);

  const cancelSession = useCallback(async () => {
    if (!session) return;
    try {
      await client.cancelSession(session.id);
      const fresh = await client.getSession(session.id);
      setSession(fresh);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [client, session]);

  const loadTools = useCallback(async () => {
    try {
      setTools(await client.listTools());
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [client]);

  const decideApproval = useCallback(
    async (approvalId: string, decision: ApprovalDecision, comment?: string) => {
      if (!session) return;
      try {
        await client.decideApproval(session.id, approvalId, {
          decision,
          decided_by: principal,
          comment,
        });
        const fresh = await client.getSession(session.id);
        setSession(fresh);
      } catch (err) {
        setError(errorMessage(err));
      }
    },
    [client, session, principal],
  );

  const clearError = useCallback(() => setError(null), []);

  return useMemo<SessionStore>(
    () => ({
      workspaceId,
      selectWorkspace,
      session,
      running,
      error,
      tools,
      runSession,
      cancelSession,
      refresh,
      loadTools,
      decideApproval,
      clearError,
    }),
    [
      workspaceId,
      selectWorkspace,
      session,
      running,
      error,
      tools,
      runSession,
      cancelSession,
      refresh,
      loadTools,
      decideApproval,
      clearError,
    ],
  );
}
